<template>
  <VDialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    width="420"
  >
    <VCard v-if="reservation" class="event-dialog">
      <VCardTitle class="event-dialog__header">
        <span class="event-dialog__title">
          <VIcon icon="mdi-calendar" size="small" class="mr-1" />
          {{ title }}
        </span>
        <VBtn
          icon="mdi-pencil"
          variant="text"
          size="small"
          @click="$emit('edit', reservation)"
        />
      </VCardTitle>
      <VCardText class="event-dialog__content">
        <span
          class="event-dialog__status"
          :style="{
            backgroundColor: statusColors.container,
            color: statusColors.onContainer,
          }"
        >
          {{ $t(`reservation_status.${reservation.status}`) }}
        </span>

        <div class="event-dialog__info-item">
          <VIcon icon="mdi-clock" size="small" />
          <span>{{ timeRange }}</span>
        </div>
        <div v-if="withNames" class="event-dialog__info-item">
          <VIcon icon="mdi-account" size="small" />
          <span>{{ withNames }}</span>
        </div>
        <div v-if="phones" class="event-dialog__info-item">
          <VIcon icon="mdi-phone" size="small" />
          <span>{{ phones }}</span>
        </div>
        <div v-if="reservation.comment" class="event-dialog__info-item">
          <VIcon icon="mdi-text" size="small" />
          <span>{{ reservation.comment }}</span>
        </div>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { Reservation } from "@/main/types/reservation.type";
import { RESERVATION_STATUS_COLORS } from "@/main/helpers/reservation-status.helper";

const props = defineProps<{
  modelValue: boolean;
  reservation: Reservation | null;
}>();

defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "edit", reservation: Reservation): void;
}>();

const statusColors = computed(
  () =>
    (props.reservation && RESERVATION_STATUS_COLORS[props.reservation.status]) ||
    RESERVATION_STATUS_COLORS.draft
);

const title = computed(
  () => props.reservation?.services.map((service) => service.name).join(", ") || ""
);

// `customers` is the sole record of who a reservation is for now - every
// reservation gets one (see Reservations::AssignCustomerService), including
// ones booked by a real logged-in account (Customer#user_id links back to
// it, used for notifications and the mobile app's own "my bookings").
const withNames = computed(() =>
  (props.reservation?.customers.map((customer) => customer.name) || [])
    .filter(Boolean)
    .join(", "),
);

const phones = computed(() =>
  (props.reservation?.customers.map((customer) => customer.phone_number) || [])
    .filter(Boolean)
    .join(", "),
);

const pad = (n: number) => n.toString().padStart(2, "0");

// Dates come from the backend as UTC ISO strings - render them in the
// browser's own local time (what `Date`'s non-UTC getters already give us)
// rather than the UTC getters previously used here, which showed the same
// wall-clock time to everyone regardless of their timezone.
const timeRange = computed(() => {
  if (!props.reservation) return "";
  const start = new Date(props.reservation.start_date);
  const end = new Date(props.reservation.end_date);
  const format = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return `${format(start)} - ${format(end)}`;
});
</script>

<style lang="scss" scoped>
.event-dialog {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__title {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: 600;
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__content {
    display: flex;
    flex-direction: column;
    row-gap: 8px;
  }

  &__status {
    align-self: flex-start;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 4px 10px;
    border-radius: 999px;
  }

  &__info-item {
    display: flex;
    align-items: flex-start;
    column-gap: 8px;
  }
}
</style>
